import { useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { AccountCircle, Send, WhatsApp } from '@mui/icons-material';
import SosOutlinedIcon from '@mui/icons-material/SosOutlined';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { sendSupportEmail } from '../lib/email/aws';
import useOnline from '../hooks/useOnline';

const UserMenu = () => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [anchorElSupport, setAnchorElSupport] = useState(null);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [sended, setSended] = useState(false);
  const [feedback, setFeedback] = useState('');
  const isOnline = useOnline();
  const url = 'https://api.whatsapp.com/send?phone=5511937139001';

  const navigate = useNavigate();
  const { t } = useTranslation('common');
  const { user, loading, legacyWebAccess, signOut } = useAuth();

  const handleMenu = (e) => {
    setAnchorEl(e.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMenuSupport = (e) => {
    setAnchorElSupport(e.currentTarget);
  };

  const handleCloseSupport = () => {
    setAnchorElSupport(null);
  };

  const handleSupportEmail = async () => {
    setSending(true);
    try {
      await sendSupportEmail(user.email, message);
      setSending(false);
      setSended(true);
      setFeedback(t('userMenu.support.feedback.success'));
    } catch {
      setSending(false);
      setSended(true);
      setFeedback(t('userMenu.support.feedback.error'));
    }
  };

  return (
    <div>
      {user && !loading && (
        <div>
          <IconButton
            size="large"
            aria-label={t('userMenu.support.ariaLabel')}
            aria-controls="menu-support"
            aria-haspopup="true"
            onClick={(e) => { handleMenuSupport(e); }}
            color="inherit"
          >
            <SosOutlinedIcon />
          </IconButton>

          <IconButton
            size="large"
            aria-label={t('userMenu.account.ariaLabel')}
            aria-controls="menu-appbar"
            aria-haspopup="true"
            onClick={(e) => { handleMenu(e); }}
            color="inherit"
          >
            <AccountCircle />
          </IconButton>

          <Menu
            id="user-menu"
            anchorEl={anchorEl}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            open={Boolean(anchorEl)}
            onClose={handleClose}
          >
            {legacyWebAccess && (
              <MenuItem onClick={() => { navigate('/dashboard'); handleClose(); }}>
                {t('userMenu.account.analysis')}
              </MenuItem>
            )}
            <MenuItem onClick={() => { navigate('/minha-conta'); handleClose(); }}>
              {t('userMenu.account.myAccount')}
            </MenuItem>
            <Divider />
            <MenuItem onClick={() => { signOut(); handleClose(); }}>
              {t('userMenu.account.signOut')}
            </MenuItem>
          </Menu>

          <Menu
            id="support-menu"
            anchorEl={anchorElSupport}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            open={Boolean(anchorElSupport)}
            onClose={handleCloseSupport}
          >
            <Box display="flex" flexDirection="column" paddingX="20px" sx={{ mt: 2 }}>
              <Typography>{t('userMenu.support.title')}</Typography>
              <Typography>{t('userMenu.support.subtitle')}</Typography>
            </Box>

            <Divider sx={{ my: 3 }} />

            {(!window.electron || isOnline) && (
              <Box display="flex" flexDirection="column" paddingX="20px">
                <Typography>{t('userMenu.support.email.title')}</Typography>
                {!sending && !sended && (
                  <>
                    <Typography variant="caption">
                      {t('userMenu.support.email.description')}
                    </Typography>
                    <TextField
                      sx={{ mt: 2 }}
                      label={t('userMenu.support.email.messageLabel')}
                      multiline
                      rows={3}
                      value={message}
                      onChange={(e) => { setMessage(e.target.value); }}
                    />

                    <Button
                      sx={{ my: 2 }}
                      variant="outlined"
                      startIcon={<Send />}
                      onClick={handleSupportEmail}
                    >
                      {t('userMenu.support.email.send')}
                    </Button>
                  </>
                )}

                {sending && !sended && <CircularProgress />}

                {!sending && sended && <>{feedback}</>}

                <Typography variant="caption">
                  {t('userMenu.support.email.replyNotice')}
                </Typography>
              </Box>
            )}

            <Divider sx={{ my: 3 }} />

            <Box display="flex" flexDirection="column" paddingX="20px">
              <Typography>{t('userMenu.support.whatsapp.title')}</Typography>
              <Typography variant="caption">+55 (11) 93713-9001</Typography>

              <Button
                sx={{ my: 2 }}
                color="success"
                startIcon={<WhatsApp />}
                variant="outlined"
                onClick={() => {
                  if (window.electron?.openExternal) {
                    window.electron?.openExternal?.(url);
                  } else {
                    window.open(url, '_blank');
                  }
                }}
              >
                WhatsApp
              </Button>

              <Typography variant="caption">
                {t('userMenu.support.whatsapp.hours')}
              </Typography>
            </Box>

          </Menu>
        </div>
      )}

      {!user && !loading && (
        <div>
          <IconButton
            size="large"
            aria-label={t('userMenu.account.ariaLabel')}
            aria-controls="menu-appbar"
            aria-haspopup="true"
            onClick={() => { window.location.href = '/login'; }}
            color="inherit"
          >
            <AccountCircle />
          </IconButton>
        </div>
      )}
    </div>
  );
};

export default UserMenu;
